import { createSlice } from '@reduxjs/toolkit';
import { createApi } from '@reduxjs/toolkit/query/react';
import queryString from 'query-string';
import type { RootState } from '../../configureStore';
import { axiosBaseQuery } from '../../utilities/rtk-query';
import { LaneSchema } from '../myLanes/myLanesTypes';
import { getDeadheadQuery } from '../myLanes/myLanesUtils';
import {
  DeadheadSearch,
  DeadheadSearchQuery,
  OrderDetails,
  StopDetails,
} from './findLoadsTypes';

const initialJoyrideState: {
  joyrideSeen: boolean;
} = {
  joyrideSeen: false,
};

export const joyrideSlice = createSlice({
  name: 'joyride',
  initialState: initialJoyrideState,
  reducers: {
    setJoyrideSeen: (state, joyrideSeen) => {
      state.joyrideSeen = joyrideSeen.payload;
    },
  },
});

export const ryanTransApi = createApi({
  reducerPath: 'ryanTransApi',
  baseQuery: axiosBaseQuery({
    baseUrl: process.env.REACT_APP_API_BASE_URL!,
  }),
  tagTypes: [],
  endpoints: (builder) => ({
    GetLoadsForLane: builder.query<DeadheadSearch[], LaneSchema>({
      queryFn: async (lane, api, _extraOptions, baseQuery) => {
        const query: DeadheadSearchQuery = getDeadheadQuery(lane);
        const state = api.getState() as RootState;
        const { ordersServiceDeadheadMigration } = state.featureFlags.flags;

        const result = await baseQuery({
          url: ordersServiceDeadheadMigration
            ? `${
                process.env.REACT_APP_ORDERS_SERVICE_URL
              }/orders/deadhead?${queryString.stringify(query)}`
            : `orders/deadhead?${queryString.stringify(query)}`,
          method: 'GET',
        });

        if ('error' in result) {
          return { error: result.error };
        }

        return {
          data: (result.data as { data: DeadheadSearch[] }).data,
        };
      },
    }),
    getOrderById: builder.query<OrderDetails | undefined, string | undefined>({
      queryFn: async (id, api, _extraOptions, baseQuery) => {
        if (!id) {
          console.log('no load id');
          throw new Error('No load id provided');
        }

        const state = api.getState() as RootState;
        const { ordersServiceDeadheadMigration } = state.featureFlags.flags;

        const result = await baseQuery({
          url: ordersServiceDeadheadMigration
            ? `${
                process.env.REACT_APP_ORDERS_SERVICE_URL
              }/orders/${encodeURIComponent(id)}/available`
            : `orders/available/${encodeURIComponent(id)}`,
          method: 'GET',
        });

        if ('error' in result) {
          return { error: result.error };
        }

        return { data: result.data as OrderDetails | undefined };
      },
    }),
  }),
});

export const milesServiceApi = createApi({
  reducerPath: 'milesServiceApi',
  baseQuery: axiosBaseQuery({
    baseUrl: process.env.REACT_APP_MILES_SERVICE_BASE_URL!,
  }),
  tagTypes: [],
  endpoints: (builder) => ({
    getData: builder.mutation<
      { calculatedMiles: number },
      { fromLocation: StopDetails; toLocation: StopDetails }
    >({
      query: ({ fromLocation, toLocation }) => {
        const fromParts = fromLocation.cityStateText
          .split(',')
          .map((part) => part.trim());
        const toParts = toLocation.cityStateText
          .split(',')
          .map((part) => part.trim());
        const routeId = `${fromParts[1]}2${toParts[1]}`;

        return {
          url: `/mileage`,
          method: 'POST',
          data: [
            {
              routeId: routeId,
              hasHazmat: false,
              avoidTolls: false,
              bordersOpen: true,
              addresses: [
                {
                  city: fromParts[0] || '',
                  state: fromParts[1] || '',
                  postalCode: fromLocation.zip || '',
                },
                {
                  city: toParts[0] || '',
                  state: toParts[1] || '',
                  postalCode: '',
                },
              ],
              mileageServiceProvider: 2, // Here MileageServiceProvider
            },
          ],
        };
      },
      transformResponse: (response: any) => {
        return { calculatedMiles: Math.round(response?.[0]?.totalMiles || 0) };
      },
    }),
  }),
});

const initialOrdersState: {
  selectedOrderId: string | undefined;
} = {
  selectedOrderId: undefined,
};

export const ordersSlice = createSlice({
  name: 'orders',
  initialState: initialOrdersState,
  reducers: {
    setSelectedOrderId: (state, selectedOrderId) => {
      state.selectedOrderId = selectedOrderId.payload;
    },
  },
});

export const { setJoyrideSeen } = joyrideSlice.actions;
export const { useGetLoadsForLaneQuery, useGetOrderByIdQuery } = ryanTransApi;
export const { setSelectedOrderId } = ordersSlice.actions;
export const { useGetDataMutation } = milesServiceApi;
